import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';

// Finance dashboard. Admin-only at the backend; this guard just
// keeps non-admins from a broken view if they nav by URL.

// Currency helpers -- compact for chip-style numbers ($1.2M), full for
// the headline numbers ($1,247,500).
function fmtFull(n) {
  return `$${Math.round(Number(n) || 0).toLocaleString('en-CA')}`;
}
function fmtCompact(n) {
  const v = Number(n) || 0;
  if (v === 0) return '$0';
  if (v < 1000) return `$${Math.round(v)}`;
  if (v < 1_000_000) return `$${Math.round(v / 1000)}K`;
  return `$${(v / 1_000_000).toFixed(1)}M`;
}

export default function Finance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sites-missing-subscription panel state. Toggled by a button on
  // the Active Sites KPI tile. Lazy-loaded so we don't pay for the
  // query unless Dave / a student opens it.
  const [missingOpen, setMissingOpen]       = useState(false);
  const [missingSites, setMissingSites]     = useState(null);
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingError, setMissingError]     = useState(null);

  // Three drill-in panels for the Row-2 KPI cards. Same lazy-load
  // pattern as missing-subscriptions above -- nothing fetched until
  // Dave actually opens a panel. Each is independently toggleable so
  // he can have multiple lists open at once if he wants to scan.
  const [lostArrOpen, setLostArrOpen]       = useState(false);
  const [lostArrData, setLostArrData]       = useState(null);
  const [atRiskOpen, setAtRiskOpen]         = useState(false);
  const [atRiskSites, setAtRiskSites]       = useState(null);
  const [outreachOpen, setOutreachOpen]     = useState(false);
  const [outreachSites, setOutreachSites]   = useState(null);

  async function openMissing() {
    setMissingOpen(true);
    if (missingSites !== null) return;  // already loaded
    setMissingLoading(true);
    try {
      const r = await axios.get('/api/finance/missing-subscriptions');
      setMissingSites(r.data?.sites || []);
    } catch (e) {
      setMissingError(e.response?.data?.error || e.message);
    } finally {
      setMissingLoading(false);
    }
  }

  async function openLostArr() {
    setLostArrOpen((v) => !v);
    if (lostArrData !== null) return;
    try {
      const r = await axios.get('/api/finance/lost-arr-sites');
      setLostArrData(r.data || { recent: [], lifetime: [] });
    } catch (_) { setLostArrData({ recent: [], lifetime: [] }); }
  }
  async function openAtRisk() {
    setAtRiskOpen((v) => !v);
    if (atRiskSites !== null) return;
    try {
      const r = await axios.get('/api/finance/at-risk-sites');
      setAtRiskSites(r.data?.sites || []);
    } catch (_) { setAtRiskSites([]); }
  }
  async function openOutreach() {
    setOutreachOpen((v) => !v);
    if (outreachSites !== null) return;
    try {
      const r = await axios.get('/api/finance/renewal-outreach-sites');
      setOutreachSites(r.data?.sites || []);
    } catch (_) { setOutreachSites([]); }
  }

  useEffect(() => {
    if (user && user.is_admin === false) {
      navigate('/settings', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/finance/dashboard')
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <section className="page"><h2>Finance</h2><p>Loading…</p></section>;
  if (error)   return <section className="page"><h2>Finance</h2><p className="error">{error}</p></section>;
  if (!data)   return null;

  // Bar chart scale: tallest bar in the 12-month renewal pipeline
  // defines 100%. Each bar's height = its value / peak * 100%.
  const peakMonthValue = Math.max(1, ...data.pipeline.monthly.map((m) => m.value));
  // Top-customer concentration line: top 10 ARR / total ARR.
  const top10Sum = data.top_customers.reduce((s, c) => s + Number(c.arr || 0), 0);
  const concentration = data.kpis.total_arr > 0
    ? Math.round((top10Sum / data.kpis.total_arr) * 100)
    : 0;
  // Module mix percent base = sum of every active module's $ (== total ARR).
  const moduleTotal = data.module_mix.reduce((s, m) => s + Number(m.value || 0), 0) || 1;

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Finance</h2>
          <div className="subtitle">
            Recurring revenue, renewal pipeline, customer concentration. Admin-only.
            Updated {new Date(data.generated_at).toLocaleString('en-CA')}.
          </div>
        </div>
      </div>

      {/* ── KPI cards ────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-4)'
      }}>
        <KpiCard
          label="Annual Recurring Revenue"
          value={fmtFull(data.kpis.total_arr)}
          subtitle={`${data.kpis.billing_sites} sites with subscription data`}
        />
        <KpiCard
          label="MRR (ARR ÷ 12)"
          value={fmtFull(data.kpis.mrr)}
          subtitle="monthly equivalent"
        />
        <KpiCard
          label="Active Sites"
          value={data.kpis.active_sites.toLocaleString()}
          subtitle={
            data.kpis.missing_billing > 0
              ? `${data.kpis.missing_billing} have no subscription on file`
              : `${data.kpis.active_customers} customers`
          }
          warn={data.kpis.missing_billing > 0}
          action={data.kpis.missing_billing > 0 ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={openMissing}
              style={{ marginTop: 'var(--space-2)' }}
            >
              {missingOpen ? 'Reload list' : 'Show sites to fix'}
            </button>
          ) : null}
        />
        <KpiCard
          label="Average per Site (billing)"
          value={fmtFull(data.kpis.avg_per_site)}
          subtitle={`${fmtFull(data.kpis.avg_per_customer)} per customer · across billing sites only`}
        />
      </div>

      {/* ── Row 2 KPIs — risk & opportunity signals ─────────────────────
          Four cards that cross-reference data Mary/Dave already track:
            - Lost ARR     (status='Inactive' sites that still have billing)
            - Sub Gap $    (Active sites w/o subscription × avg-per-site)
            - At-Risk ARR  (Active sites quiet in xsiteio >30d)
            - Outreach %   (renewals due in 30d that have been emailed) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-4)'
      }}>
        {/* Lost ARR — last 12 months. Click opens the drill-in panel
            below this row showing every inactive site behind the number. */}
        <KpiCard
          onClick={openLostArr}
          active={lostArrOpen}
          label="Lost ARR (last 12 months)"
          value={fmtFull(data.churn?.lost_arr || 0)}
          subtitle={(() => {
            const recent = data.churn?.site_count || 0;
            const recentCust = data.churn?.customer_count || 0;
            const lifeArr = data.churn?.lifetime?.lost_arr || 0;
            const lifeSites = data.churn?.lifetime?.site_count || 0;
            if (recent === 0 && lifeSites === 0) {
              return 'No inactive sites with billing on file';
            }
            const recentPart = recent > 0
              ? `${recent} site${recent === 1 ? '' : 's'} · ${recentCust} customer${recentCust === 1 ? '' : 's'}`
              : 'No churn in last 12 months';
            const lifePart = lifeSites > 0
              ? `Lifetime: ${fmtFull(lifeArr)} · ${lifeSites} site${lifeSites === 1 ? '' : 's'}`
              : '';
            return [recentPart, lifePart].filter(Boolean).join('  ·  ');
          })()}
          tone={(data.churn?.lost_arr || 0) > 0 ? 'bad' : 'neutral'}
        />

        {/* Subscription Gap — potential ARR locked behind data entry.
            Estimated using avg-per-site as the multiplier; not a
            guarantee but a useful "if we cleaned this up, here's the
            upside" anchor. */}
        <KpiCard
          label="Subscription Gap"
          value={fmtFull(data.subscription_gap?.potential_arr || 0)}
          subtitle={
            (data.subscription_gap?.sites_missing || 0) > 0
              ? `${data.subscription_gap.sites_missing} sites @ ${fmtFull(data.subscription_gap.avg_per_site_used)} avg`
              : 'All Active sites have subscription data'
          }
          warn={(data.subscription_gap?.sites_missing || 0) > 0}
          action={(data.subscription_gap?.sites_missing || 0) > 0 ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={openMissing}
              style={{ marginTop: 'var(--space-2)' }}
            >
              {missingOpen ? 'Reload list' : 'Show sites to fix'}
            </button>
          ) : null}
        />

        {/* At-Risk ARR — click opens drill-in list of quiet sites. */}
        <KpiCard
          onClick={openAtRisk}
          active={atRiskOpen}
          label="At-Risk ARR (quiet ≥30d)"
          value={fmtFull(data.at_risk?.arr || 0)}
          subtitle={
            (data.at_risk?.site_count || 0) > 0
              ? `${data.at_risk.site_count} sites quiet · ${data.at_risk.customer_count} customers`
              : 'All matched billing sites active'
          }
          tone={(data.at_risk?.arr || 0) > 0 ? 'bad' : 'neutral'}
        />

        {/* Renewal Outreach — click opens drill-in list of next-30-day
            sites with their email-sent status. */}
        <KpiCard
          onClick={openOutreach}
          active={outreachOpen}
          label="Renewal Outreach (next 30d)"
          value={`${data.renewal_outreach?.coverage_pct ?? 0}%`}
          subtitle={
            (data.renewal_outreach?.total_sites || 0) > 0
              ? `${data.renewal_outreach.reached_sites} of ${data.renewal_outreach.total_sites} sites emailed`
              : 'No renewals due in next 30 days'
          }
          tone={(() => {
            const pct = data.renewal_outreach?.coverage_pct ?? 0;
            const total = data.renewal_outreach?.total_sites || 0;
            if (total === 0) return 'neutral';
            if (pct >= 75) return 'good';
            if (pct >= 40) return 'warn';
            return 'bad';
          })()}
          action={(data.renewal_outreach?.total_sites || 0) > 0 ? (
            <div style={{
              marginTop: 'var(--space-2)',
              height: 6,
              background: 'var(--color-border)',
              borderRadius: 3,
              overflow: 'hidden'
            }}>
              <div
                style={{
                  width: `${Math.min(100, data.renewal_outreach?.coverage_pct || 0)}%`,
                  height: '100%',
                  background: (() => {
                    const pct = data.renewal_outreach?.coverage_pct || 0;
                    if (pct >= 75) return 'var(--color-good)';
                    if (pct >= 40) return 'var(--color-warn)';
                    return 'var(--color-bad)';
                  })(),
                  transition: 'width 240ms ease'
                }}
              />
            </div>
          ) : null}
        />
      </div>

      {/* ── Lost ARR drill-in (last 12 months + lifetime history) ──── */}
      {lostArrOpen && lostArrData && (
        <div className="panel" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
            <h3 style={{ margin: 0 }}>Lost ARR — recent churn</h3>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setLostArrOpen(false)}>Hide</button>
          </div>
          <DrillTable
            rows={lostArrData.recent}
            emptyText="No churn captured in the last 12 months. (Sites without status_changed_at fall under Lifetime below.)"
            columns={[
              { key: 'company',           label: 'Customer' },
              { key: 'site_name',         label: 'Site' },
              { key: 'city',              label: 'City' },
              { key: 'status_changed_at', label: 'Deactivated', fmt: 'date' },
              { key: 'arr',               label: 'ARR',         fmt: 'money', align: 'right' }
            ]}
            navigate={navigate}
          />
          {lostArrData.lifetime && lostArrData.lifetime.length > lostArrData.recent.length && (
            <>
              <h4 style={{ marginTop: 'var(--space-4)' }}>Lifetime — every inactive site with billing</h4>
              <DrillTable
                rows={lostArrData.lifetime}
                emptyText="No inactive sites with billing on file."
                columns={[
                  { key: 'company',           label: 'Customer' },
                  { key: 'site_name',         label: 'Site' },
                  { key: 'city',              label: 'City' },
                  { key: 'status_changed_at', label: 'Deactivated', fmt: 'date' },
                  { key: 'arr',               label: 'ARR',         fmt: 'money', align: 'right' }
                ]}
                navigate={navigate}
              />
            </>
          )}
        </div>
      )}

      {/* ── At-Risk ARR drill-in (quiet xsiteio sites) ──────────────── */}
      {atRiskOpen && atRiskSites && (
        <div className="panel" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
            <h3 style={{ margin: 0 }}>At-Risk Sites — quiet ≥ 30 days</h3>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setAtRiskOpen(false)}>Hide</button>
          </div>
          <DrillTable
            rows={atRiskSites}
            emptyText="No quiet sites among matched xsiteio customers. (Tighter — try lowering the 30-day threshold.)"
            columns={[
              { key: 'company',            label: 'Customer' },
              { key: 'site_name',          label: 'Site' },
              { key: 'city',               label: 'City' },
              { key: 'xsiteio_last_login', label: 'Last Login', fmt: 'date' },
              { key: 'days_quiet',         label: 'Days Quiet', align: 'right' },
              { key: 'arr',                label: 'ARR',        fmt: 'money', align: 'right' }
            ]}
            navigate={navigate}
          />
        </div>
      )}

      {/* ── Renewal Outreach drill-in (next 30d sites, email status) ── */}
      {outreachOpen && outreachSites && (
        <div className="panel" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
            <h3 style={{ margin: 0 }}>Renewals — next 30 days (uncontacted first)</h3>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setOutreachOpen(false)}>Hide</button>
          </div>
          <DrillTable
            rows={outreachSites}
            emptyText="No renewals due in the next 30 days."
            columns={[
              { key: 'company',                     label: 'Customer' },
              { key: 'site_name',                   label: 'Site' },
              { key: 'earliest_renewal',            label: 'Renews',      fmt: 'date' },
              { key: 'arr',                         label: 'ARR',         fmt: 'money', align: 'right' },
              { key: 'price_increase_email_sent',   label: 'Email sent',  fmt: 'date_or_pending' }
            ]}
            navigate={navigate}
          />
        </div>
      )}

      {/* ── Sites missing subscription data (lazy panel) ──────────────
          Opens via the "Show sites to fix" button on the Active Sites
          tile. Each row links to the customer detail so students can
          click in, fill the subscription, save, and the panel can be
          reloaded to see progress. */}
      {missingOpen && (
        <div className="panel" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
            <h3 style={{ margin: 0 }}>
              Active sites missing subscription data
              {missingSites != null && (
                <span style={{ marginLeft: 'var(--space-2)', color: 'var(--color-text-dim)', fontWeight: 'var(--font-regular)' }}>
                  ({missingSites.length})
                </span>
              )}
            </h3>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setMissingOpen(false)}
            >
              Hide
            </button>
          </div>
          <p className="result-count" style={{ marginBottom: 'var(--space-3)' }}>
            Click any row to open the customer detail — add a subscription, save, then click <strong>Reload list</strong> back on the tile.
          </p>
          {missingLoading && <p>Loading…</p>}
          {missingError && <p className="error">{missingError}</p>}
          {!missingLoading && missingSites != null && missingSites.length === 0 && (
            <p className="panel-empty">Nothing to fix — every Active site has subscription data.</p>
          )}
          {!missingLoading && missingSites != null && missingSites.length > 0 && (
            <div style={{ overflow: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Customer</th>
                    <th style={{ textAlign: 'left' }}>Site Name</th>
                    <th style={{ textAlign: 'left' }}>Site #</th>
                    <th style={{ textAlign: 'left' }}>City</th>
                    <th style={{ textAlign: 'left' }}>Province</th>
                    <th style={{ textAlign: 'left' }}>Fuel Brand</th>
                    <th style={{ textAlign: 'left' }}>POS</th>
                  </tr>
                </thead>
                <tbody>
                  {missingSites.map((s) => (
                    <tr
                      key={s.site_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/customers/${s.customer_id}`)}
                      title={`Open ${s.company || '(unknown customer)'}`}
                    >
                      <td>
                        <Link to={`/customers/${s.customer_id}`}
                              onClick={(e) => e.stopPropagation()}
                              style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', textDecoration: 'none' }}>
                          {s.company || `Customer #${s.customer_id}`}
                        </Link>
                      </td>
                      <td>{s.site_name || '—'}</td>
                      <td className="cell-mono">{s.site_number || '—'}</td>
                      <td>{s.city || '—'}</td>
                      <td>{s.province_state || '—'}</td>
                      <td>{s.fuel_brand || '—'}</td>
                      <td>{s.pos_type || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Renewal pipeline ─────────────────────────────────────────── */}
      <div className="panel" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <h3 style={{ marginTop: 0 }}>Renewal pipeline — next 12 months</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 'var(--space-2)',
          alignItems: 'end',
          marginTop: 'var(--space-3)',
          minHeight: 180
        }}>
          {data.pipeline.monthly.map((m) => {
            const heightPct = peakMonthValue > 0 ? (m.value / peakMonthValue) * 100 : 0;
            return (
              <div key={m.month_key} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4
              }}>
                <div style={{
                  fontSize: 'var(--text-small)',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-text-dim)',
                  whiteSpace: 'nowrap'
                }}>{fmtCompact(m.value)}</div>
                <div style={{
                  width: '100%',
                  height: `${Math.max(heightPct, 2)}%`,
                  minHeight: 4,
                  background: 'var(--color-accent, #0e7490)',
                  borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                  transition: 'height 0.3s ease',
                  alignSelf: 'flex-end'
                }} title={`${m.month_label}: ${fmtFull(m.value)} · ${m.sites} site${m.sites === 1 ? '' : 's'}`} />
                <div style={{
                  fontSize: 'var(--text-caption)',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-text-dim)',
                  whiteSpace: 'nowrap'
                }}>{m.month_label.replace(/ \d{4}$/, '')}</div>
              </div>
            );
          })}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-4)',
          paddingTop: 'var(--space-3)',
          borderTop: '1px solid var(--color-border)'
        }}>
          <PipelineStat
            tone="red"
            value={fmtFull(data.pipeline.overdue_value)}
            label={`overdue — ${data.pipeline.overdue_sites} site${data.pipeline.overdue_sites === 1 ? '' : 's'}`}
          />
          <PipelineStat
            tone="amber"
            value={fmtFull(data.pipeline.due_30_value)}
            label={`due in 30 days — ${data.pipeline.due_30_sites} site${data.pipeline.due_30_sites === 1 ? '' : 's'}`}
          />
          <PipelineStat
            tone="teal"
            value={fmtFull(data.pipeline.due_90_value)}
            label="due in 90 days (rolling)"
          />
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.4fr 1fr',
        gap: 'var(--space-4)',
        marginBottom: 'var(--space-4)'
      }}>
        {/* ── Top 10 customers ─────────────────────────────────────── */}
        <div className="panel" style={{ padding: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
            <h3 style={{ margin: 0 }}>Top 10 customers by ARR</h3>
            <span className="result-count">
              Top 10 = {concentration}% of total ARR
            </span>
          </div>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', width: 40 }}>#</th>
                <th style={{ textAlign: 'left' }}>Customer</th>
                <th style={{ textAlign: 'right', width: 80 }}>Sites</th>
                <th style={{ textAlign: 'right' }}>ARR</th>
                <th style={{ textAlign: 'left', width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.top_customers.map((c, i) => {
                const sharePct = data.kpis.total_arr > 0 ? (c.arr / data.kpis.total_arr) * 100 : 0;
                return (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/customers/${c.id}`)}>
                    <td className="cell-mono" style={{ color: 'var(--color-text-dim)' }}>{i + 1}</td>
                    <td>
                      <Link to={`/customers/${c.id}`} style={{ color: 'var(--color-text)', fontWeight: 'var(--font-semibold)', textDecoration: 'none' }}>
                        {c.company}
                      </Link>
                      {c.fuel_brand && (
                        <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                          · {c.fuel_brand}
                        </span>
                      )}
                    </td>
                    <td className="cell-mono" style={{ textAlign: 'right' }}>{c.site_count}</td>
                    <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 'var(--font-semibold)' }}>{fmtFull(c.arr)}</td>
                    <td>
                      <div style={{
                        background: 'var(--color-border)',
                        borderRadius: 'var(--radius-pill)',
                        height: 8,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${Math.min(100, sharePct * 4)}%`,    // 25% ARR -> full bar
                          height: '100%',
                          background: 'var(--color-accent, #0e7490)'
                        }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Module mix ───────────────────────────────────────────── */}
        <div className="panel" style={{ padding: 'var(--space-4)' }}>
          <h3 style={{ marginTop: 0 }}>Revenue by module</h3>
          <div style={{ marginTop: 'var(--space-3)' }}>
            {data.module_mix.map((m) => {
              const pct = (m.value / moduleTotal) * 100;
              return (
                <div key={m.key} style={{ marginBottom: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-small)' }}>
                    <span style={{ fontWeight: 'var(--font-semibold)' }}>{m.label}</span>
                    <span className="cell-mono" style={{ color: 'var(--color-text-dim)' }}>
                      {fmtCompact(m.value)} · {pct.toFixed(1)}%
                    </span>
                  </div>
                  <div style={{
                    background: 'var(--color-border)',
                    borderRadius: 'var(--radius-pill)',
                    height: 8,
                    marginTop: 2,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: 'var(--color-accent, #0e7490)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function KpiCard({ label, value, subtitle, warn = false, tone, action = null, onClick = null, active = false }) {
  // tone (optional) sets the value text colour for risk/health signals:
  //   bad     -> red    (Lost ARR, At-Risk ARR)
  //   warn    -> amber  (Subscription Gap)
  //   good    -> green  (Renewal Outreach >= 75%)
  //   neutral -> default (no tone applied)
  // Falls back to warn=true behaviour for backwards-compat with existing callers.
  const effectiveTone = tone || (warn ? 'warn' : 'neutral');
  const valueColor = effectiveTone === 'bad'  ? 'var(--color-bad)'
                   : effectiveTone === 'warn' ? 'var(--color-warn)'
                   : effectiveTone === 'good' ? 'var(--color-good)'
                   :                            'var(--color-text)';
  // Clickable cards get a subtle outline + pointer + hover lift so it's
  // obvious which numbers drill in. Static cards (ARR, MRR, etc.) stay
  // as-is. Active state outlines the card in accent so users see which
  // panel they're looking at below.
  const clickable = typeof onClick === 'function';
  return (
    <div
      className="panel"
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      style={{
        padding: 'var(--space-3)',
        cursor: clickable ? 'pointer' : undefined,
        outline: active ? '2px solid var(--color-accent)' : undefined,
        outlineOffset: active ? '-2px' : undefined,
        transition: 'transform 80ms ease, box-shadow 80ms ease'
      }}
    >
      <div style={{ fontSize: 'var(--text-caption)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-dim)', fontWeight: 'var(--font-semibold)' }}>
        {label}
      </div>
      <div style={{ fontSize: 'var(--text-page, 22px)', fontFamily: 'var(--font-mono)', fontWeight: 'var(--font-bold)', marginTop: 4, color: valueColor }}>
        {value}
      </div>
      {subtitle && (
        <div style={{
          fontSize: 'var(--text-small)',
          marginTop: 4,
          // Amber when the subtitle is calling out a data-quality gap
          // (e.g. "96 have no subscription on file"). Keeps it visible
          // without being alarming red.
          color: warn ? 'var(--color-warn, #b45309)' : 'var(--color-text-dim)',
          fontWeight: warn ? 'var(--font-semibold)' : 'var(--font-regular)'
        }}>
          {subtitle}
        </div>
      )}
      {action}
    </div>
  );
}

// Compact drill-in table used by the three KPI lazy panels. Renders
// rows with a small column dictionary so each panel can pick its own
// shape without three separate tables full of duplicated styling.
// Clicking a row navigates to that customer; click stopPropagation on
// the row prevents the parent panel-click from swallowing the nav.
function DrillTable({ rows, columns, emptyText, navigate }) {
  if (!rows || rows.length === 0) {
    return <p className="cell-muted">{emptyText}</p>;
  }
  function fmtCell(row, col) {
    const v = row[col.key];
    if (v == null || v === '') return '—';
    if (col.fmt === 'money') {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);
    }
    if (col.fmt === 'date') {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10);
    }
    if (col.fmt === 'date_or_pending') {
      if (!v) return <span style={{ color: 'var(--color-bad)' }}>Not sent</span>;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? '—' :
        <span style={{ color: 'var(--color-good)' }}>{d.toISOString().slice(0, 10)}</span>;
    }
    return String(v);
  }
  return (
    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ textAlign: c.align || 'left' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.site_id}
              onClick={() => navigate && row.customer_id && navigate(`/customers/${row.customer_id}`)}
              style={{ cursor: row.customer_id ? 'pointer' : undefined }}
            >
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align || 'left', fontFamily: ['money','date','date_or_pending'].includes(c.fmt) ? 'var(--font-mono)' : undefined }}>
                  {fmtCell(row, c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PipelineStat({ tone, value, label }) {
  const toneColor = tone === 'red'   ? 'var(--color-bad, #b91c1c)'
                 : tone === 'amber' ? 'var(--color-warn, #b45309)'
                 :                    'var(--color-accent, #0e7490)';
  return (
    <div>
      <div style={{ fontSize: 'var(--text-title, 20px)', fontFamily: 'var(--font-mono)', fontWeight: 'var(--font-bold)', color: toneColor }}>
        {value}
      </div>
      <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>{label}</div>
    </div>
  );
}
